/// <amd-module name="Feed/Core/Components/FeedDialog" />
import * as React from "react";
import { connect } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";

import { VssComponent } from "VSS/Platform/Layout";

import { IButtonProps } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import * as PackageResources from "Feed/Common/Resources";
import { FeedDialogData, FeedHubActions, getFeedHubModules, IFeedHubViewAwareState } from "Feed/Core/Redux/FeedHub";

export interface IFeedDialogProps {
    feedDialogData: FeedDialogData | undefined;
}

const Actions = {
    dialogLoading: FeedHubActions.dialogLoading,
    dismissDialog: FeedHubActions.dismissDialog
};

class UnconnectedFeedDialog extends VssComponent<IFeedDialogProps & typeof Actions> {
    public render() {
        if (this.props.feedDialogData === undefined) {
            return null;
        }
        const dialogData = this.props.feedDialogData;

        const callbackAndDismissDialog = () => {
            dialogData!.onDismissCallback!();
            this.props.dismissDialog();
        };

        return (
            <Dialog
                titleProps={{ text: dialogData.headerText }}
                className={"feed-dialog " + dialogData.className}
                footerButtonProps={this.getButtons(callbackAndDismissDialog)}
                onDismiss={callbackAndDismissDialog}
            >
                {dialogData.warningBody ? (
                    <MessageCard className="dialog-warning-card" severity={MessageCardSeverity.Warning}>
                        {dialogData.warningBody}
                    </MessageCard>
                ) : null}
                {dialogData.dialogBody}
            </Dialog>
        );
    }

    private getButtons(callbackAndDismissDialog: () => void): IButtonProps[] {
        const dialogData = this.props.feedDialogData!;
        let footerButtons: IButtonProps[] = [
            {
                text: dialogData.dismissButtonText || PackageResources.Dialog_CancelButtonText,
                className: "cancel-button",
                onClick: callbackAndDismissDialog
            }
        ];

        if (dialogData.primaryButtonText && dialogData.primaryButtonCallback) {
            footerButtons.push(
                {
                    text:
                        dialogData.isLoading && dialogData.primaryButtonLoadingText
                            ? dialogData.primaryButtonLoadingText
                            : dialogData.primaryButtonText,
                    className: dialogData.primaryButtonClassName,
                    danger: dialogData.primaryButtonDanger,
                    iconProps:
                        // Render a loading spinner next to text if loading text was supplied
                        dialogData.isLoading && dialogData.primaryButtonLoadingText
                            ? {
                                render: () => {
                                    return (
                                        <Spinner
                                            size={SpinnerSize.xSmall}
                                            className="feed-dialog-primary-spinner"
                                        />
                                    );
                                }
                            }
                            : undefined,
                    onClick: () => {
                        this.props.dialogLoading();
                        dialogData.primaryButtonCallback!();
                    }
                }
            );
        }

        return footerButtons;
    }
}

function mapStateToProps(state: IFeedHubViewAwareState): IFeedDialogProps {
    return { feedDialogData: state.ui.dialogData };
}

const ConnectedFeedDialog = connect(mapStateToProps, Actions)(UnconnectedFeedDialog);

export const FeedDialog = () => (
    <DynamicModuleLoader modules={getFeedHubModules()}>
        <ConnectedFeedDialog />
    </DynamicModuleLoader>
);
